import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Lightning, ClipboardText, Plus, MagnifyingGlass,
  Warning, Clock, CheckCircle, CaretDown, XCircle,
  ArrowsClockwise, User, CalendarBlank, Flag, ChatDots,
  Funnel, X, CaretRight, ArrowUp, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODULES = ['BOQ', 'Project', 'WorkOrder', 'PurchaseOrder', 'MaterialTracking', 'Manpower', 'Gantt', 'Execution', 'General'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const STATUSES = ['Pending', 'In Progress', 'Completed', 'Overdue', 'Cancelled'];

const PRIORITY_COLORS = {
  Low: 'bg-zinc-100 text-zinc-700',
  Medium: 'bg-blue-50 text-blue-700',
  High: 'bg-amber-50 text-amber-700',
  Critical: 'bg-red-50 text-red-700',
};

const STATUS_COLORS = {
  Pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Completed: 'bg-green-50 text-green-700 border-green-200',
  Overdue: 'bg-red-50 text-red-700 border-red-200',
  Cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const ESCALATION_LABELS = { 0: '', 1: 'L1 Reminder', 2: 'L2 Escalated', 3: 'L3 Management' };

const Badge = ({ className, children, ...props }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${className}`} {...props}>{children}</span>
);

export const ActionCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [projects, setProjects] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [commentText, setCommentText] = useState('');
  const searchTimeout = useRef(null);

  // New task form
  const [form, setForm] = useState({
    title: '', description: '', linked_module: 'General', priority: 'Medium',
    assigned_to: '', project_id: '', due_date: '',
  });

  const fetchAll = useCallback(async () => {
    try {
      const [tasksRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tasks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tasks/summary`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    // Fetch users and projects for dropdowns
    const fetchMeta = async () => {
      try {
        const [usersRes, projectsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setUsers(usersRes.data);
        setProjects(projectsRes.data);
      } catch {}
    };
    fetchMeta();
  }, [fetchAll]);

  const handleSearch = useCallback((val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (val) params.append('search', val);
        const res = await axios.get(`${BACKEND_URL}/api/tasks?${params}`, { withCredentials: true });
        setTasks(res.data);
      } catch {}
    }, 400);
  }, []);

  const filteredTasks = tasks.filter(t => {
    if (activeTab === 'my' && t.assigned_to !== user?.id) return false;
    if (activeTab === 'assigned' && t.assigned_by !== user?.id) return false;
    if (activeTab === 'overdue' && t.status !== 'Overdue') return false;
    if (activeTab === 'critical' && !['High', 'Critical'].includes(t.priority)) return false;
    if (filterModule && t.linked_module !== filterModule) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterProject && t.project_id !== filterProject) return false;
    return true;
  });

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      const payload = { ...form };
      if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
      else delete payload.due_date;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.project_id) delete payload.project_id;
      await axios.post(`${BACKEND_URL}/api/tasks`, payload, { withCredentials: true });
      toast.success('Task created');
      setCreateOpen(false);
      setForm({ title: '', description: '', linked_module: 'General', priority: 'Medium', assigned_to: '', project_id: '', due_date: '' });
      fetchAll();
    } catch { toast.error('Failed to create task'); }
  };

  const quickStatusUpdate = async (taskId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      fetchAll();
      if (detailTask?.id === taskId) {
        const res = await axios.get(`${BACKEND_URL}/api/tasks/${taskId}`, { withCredentials: true });
        setDetailTask(res.data);
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch { toast.error('Failed to update'); }
  };

  const handleEscalate = async (taskId, level) => {
    try {
      await axios.put(`${BACKEND_URL}/api/tasks/${taskId}`, { escalation_level: level }, { withCredentials: true });
      fetchAll();
      toast.success(`Escalated to Level ${level}`);
    } catch { toast.error('Failed to escalate'); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !detailTask) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tasks/${detailTask.id}/comments`, { text: commentText }, { withCredentials: true });
      setDetailTask(res.data);
      setCommentText('');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`, { withCredentials: true });
      toast.success('Task deleted');
      if (detailTask?.id === taskId) setDetailTask(null);
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const runSystemScan = async () => {
    try {
      const [overdue, generated] = await Promise.all([
        axios.post(`${BACKEND_URL}/api/tasks/check-overdue`, {}, { withCredentials: true }),
        axios.post(`${BACKEND_URL}/api/tasks/generate-system-tasks`, {}, { withCredentials: true }),
      ]);
      toast.success(`Scan complete: ${generated.data.created} new tasks, ${overdue.data.overdue_marked} marked overdue`);
      fetchAll();
    } catch { toast.error('Scan failed'); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (t) => t.due_date && new Date(t.due_date) < new Date() && !['Completed', 'Cancelled'].includes(t.status);

  const tabs = [
    { key: 'all', label: 'All Tasks', count: tasks.length },
    { key: 'my', label: 'My Tasks', count: summary.my_pending || 0 },
    { key: 'assigned', label: 'Assigned by Me', count: summary.assigned_by_me || 0 },
    { key: 'overdue', label: 'Overdue', count: summary.total_overdue || 0 },
    { key: 'critical', label: 'High / Critical', count: (summary.critical_count || 0) + (summary.high_count || 0) },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-500">Loading Action Center...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Lightning size={24} weight="fill" className="text-amber-500" />
              <div>
                <h1 className="text-xl font-bold tracking-tight text-zinc-900" data-testid="action-center-title">Action Center</h1>
                <p className="text-xs text-zinc-500 mt-0.5">Track tasks, deadlines, and escalations. Complete tasks on time to keep projects on track.</p>
              </div>
            </div>
            <div className="flex gap-2">
              {['admin', 'super_admin'].includes(user?.role) && (
                <Button onClick={runSystemScan} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 text-xs" data-testid="system-scan-btn">
                  <ArrowsClockwise size={16} className="mr-1.5" /> System Scan
                </Button>
              )}
              <Button onClick={() => setCreateOpen(true)} className="bg-zinc-900 text-white rounded-none hover:bg-zinc-800 text-xs" data-testid="create-task-btn">
                <Plus size={16} className="mr-1.5" /> New Task
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
          {[
            { label: 'My Pending', value: summary.my_pending || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'My Overdue', value: summary.my_overdue || 0, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Assigned by Me', value: summary.assigned_by_me || 0, color: 'text-violet-600', bg: 'bg-violet-50' },
            { label: 'Total Overdue', value: summary.total_overdue || 0, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Critical', value: summary.critical_count || 0, color: 'text-red-700', bg: 'bg-red-50' },
            { label: 'High Priority', value: summary.high_count || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((card, i) => (
            <div key={i} className={`${card.bg} border border-zinc-200 rounded-sm p-3`} data-testid={`summary-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
              <div className="text-[11px] text-zinc-600 font-medium mt-0.5">{card.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs + Search + Filters */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2 flex-wrap gap-2">
            <div className="flex gap-0.5 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`tab-${tab.key}`}
                  className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}
                >
                  {tab.label} <span className="ml-1 opacity-60">({tab.count})</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-2 text-zinc-400" />
                <Input
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search tasks..."
                  className="pl-8 h-8 text-xs w-48 rounded-none border-zinc-300"
                  data-testid="search-input"
                />
              </div>
              <Button variant="outline" size="sm" className="rounded-none border-zinc-300 h-8" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
                <Funnel size={14} className="mr-1" /> Filters
              </Button>
            </div>
          </div>

          {/* Filter row */}
          {showFilters && (
            <div className="flex gap-2 items-center px-4 py-2 border-b border-zinc-100 bg-zinc-50 flex-wrap" data-testid="filter-row">
              <select value={filterModule} onChange={e => setFilterModule(e.target.value)} className="text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="filter-module">
                <option value="">All Modules</option>
                {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="filter-priority">
                <option value="">All Priorities</option>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="filter-status">
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterProject} onChange={e => setFilterProject(e.target.value)} className="text-xs border border-zinc-300 px-2 py-1.5 bg-white" data-testid="filter-project">
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number}</option>)}
              </select>
              <button onClick={() => { setFilterModule(''); setFilterPriority(''); setFilterStatus(''); setFilterProject(''); }} className="text-xs text-zinc-500 hover:text-zinc-800 underline">Clear</button>
            </div>
          )}

          {/* Task List */}
          <div className="divide-y divide-zinc-100">
            {filteredTasks.length === 0 ? (
              <div className="py-12 text-center" data-testid="no-tasks">
                <ClipboardText size={36} className="mx-auto text-zinc-300 mb-2" />
                <p className="text-sm font-medium text-zinc-600">No tasks found</p>
                <p className="text-xs text-zinc-400 mt-1">
                  {activeTab === 'overdue' ? 'No overdue tasks — great job keeping up!' :
                   activeTab === 'critical' ? 'No high or critical priority tasks at the moment.' :
                   'Create a task or run a System Scan to auto-detect pending items.'}
                </p>
              </div>
            ) : filteredTasks.map(task => (
              <div
                key={task.id}
                className={`flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 cursor-pointer transition-colors group ${isOverdue(task) ? 'border-l-2 border-red-400' : ''}`}
                onClick={() => setDetailTask(task)}
                data-testid={`task-row-${task.task_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-zinc-400">{task.task_id}</span>
                    <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                    <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                    {task.escalation_level > 0 && (
                      <Badge className="bg-orange-50 text-orange-700 border-orange-200">{ESCALATION_LABELS[task.escalation_level]}</Badge>
                    )}
                    {task.is_system_generated && <Badge className="bg-violet-50 text-violet-600 border-violet-200">Auto</Badge>}
                    <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-200">{task.linked_module}</span>
                  </div>
                  <h3 className="text-sm font-medium text-zinc-900 mt-1 truncate">{task.title}</h3>
                  {task.project_name && <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{task.project_name}</p>}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500 shrink-0">
                  {task.assigned_to_name && (
                    <div className="flex items-center gap-1"><User size={12} /> {task.assigned_to_name}</div>
                  )}
                  <div className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-500 font-semibold' : ''}`}>
                    <CalendarBlank size={12} /> {formatDate(task.due_date)}
                  </div>
                  {/* Quick actions */}
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1" onClick={e => e.stopPropagation()}>
                    {task.status === 'Pending' && (
                      <button onClick={() => quickStatusUpdate(task.id, 'In Progress')} className="p-1 hover:bg-blue-50 text-blue-600" title="Start" data-testid={`start-${task.task_id}`}>
                        <CaretRight size={14} weight="bold" />
                      </button>
                    )}
                    {['Pending', 'In Progress', 'Overdue'].includes(task.status) && (
                      <button onClick={() => quickStatusUpdate(task.id, 'Completed')} className="p-1 hover:bg-green-50 text-green-600" title="Complete" data-testid={`complete-${task.task_id}`}>
                        <CheckCircle size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreateOpen(false)}>
          <div className="bg-white w-full max-w-lg rounded-sm shadow-xl" onClick={e => e.stopPropagation()} data-testid="create-task-modal">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
              <h2 className="text-sm font-bold text-zinc-900">Create Task</h2>
              <button onClick={() => setCreateOpen(false)}><X size={18} className="text-zinc-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Title *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="task-title-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 w-full border border-zinc-300 px-3 py-2 text-sm resize-none h-20" data-testid="task-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Module</label>
                  <select value={form.linked_module} onChange={e => setForm({...form, linked_module: e.target.value})} className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="task-module-select">
                    {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Priority</label>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="task-priority-select">
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Assign To</label>
                  <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="task-assignee-select">
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Due Date</label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="task-due-date" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Project (optional)</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="task-project-select">
                  <option value="">No project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
              <Button onClick={handleCreate} className="bg-zinc-900 text-white rounded-none hover:bg-zinc-800 text-xs" data-testid="submit-task-btn">Create Task</Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Slideout */}
      {detailTask && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setDetailTask(null)}>
          <div className="bg-white w-full max-w-md shadow-xl flex flex-col" onClick={e => e.stopPropagation()} data-testid="task-detail-panel">
            {/* Header */}
            <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-zinc-400">{detailTask.task_id}</span>
                  <Badge className={PRIORITY_COLORS[detailTask.priority]}>{detailTask.priority}</Badge>
                  <Badge className={STATUS_COLORS[detailTask.status]}>{detailTask.status}</Badge>
                </div>
                <h2 className="text-sm font-bold text-zinc-900 mt-1.5">{detailTask.title}</h2>
              </div>
              <button onClick={() => setDetailTask(null)}><X size={18} className="text-zinc-400" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {detailTask.description && (
                <div>
                  <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">Description</label>
                  <p className="text-sm text-zinc-700 mt-1">{detailTask.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-zinc-500">Module:</span> <span className="font-medium">{detailTask.linked_module}</span></div>
                <div><span className="text-zinc-500">Assigned To:</span> <span className="font-medium">{detailTask.assigned_to_name || 'Unassigned'}</span></div>
                <div><span className="text-zinc-500">Assigned By:</span> <span className="font-medium">{detailTask.assigned_by_name}</span></div>
                <div><span className="text-zinc-500">Start:</span> <span className="font-medium">{formatDate(detailTask.start_date)}</span></div>
                <div><span className="text-zinc-500">Due:</span> <span className={`font-medium ${isOverdue(detailTask) ? 'text-red-600' : ''}`}>{formatDate(detailTask.due_date)}</span></div>
                {detailTask.completed_date && <div><span className="text-zinc-500">Completed:</span> <span className="font-medium text-green-600">{formatDate(detailTask.completed_date)}</span></div>}
                {detailTask.project_name && <div className="col-span-2"><span className="text-zinc-500">Project:</span> <span className="font-medium">{detailTask.project_name}</span></div>}
                {detailTask.escalation_level > 0 && <div className="col-span-2"><span className="text-zinc-500">Escalation:</span> <Badge className="bg-orange-50 text-orange-700 border-orange-200">{ESCALATION_LABELS[detailTask.escalation_level]}</Badge></div>}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 flex-wrap">
                {detailTask.status === 'Pending' && <Button size="sm" variant="outline" className="rounded-none text-xs border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => quickStatusUpdate(detailTask.id, 'In Progress')} data-testid="detail-start-btn">Start</Button>}
                {['Pending', 'In Progress', 'Overdue'].includes(detailTask.status) && <Button size="sm" variant="outline" className="rounded-none text-xs border-green-200 text-green-700 hover:bg-green-50" onClick={() => quickStatusUpdate(detailTask.id, 'Completed')} data-testid="detail-complete-btn">Complete</Button>}
                {['Pending', 'In Progress', 'Overdue'].includes(detailTask.status) && <Button size="sm" variant="outline" className="rounded-none text-xs border-zinc-200 text-zinc-600 hover:bg-zinc-50" onClick={() => quickStatusUpdate(detailTask.id, 'Cancelled')} data-testid="detail-cancel-btn">Cancel</Button>}
                {detailTask.escalation_level < 3 && ['Pending', 'In Progress', 'Overdue'].includes(detailTask.status) && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs border-orange-200 text-orange-700 hover:bg-orange-50" onClick={() => handleEscalate(detailTask.id, detailTask.escalation_level + 1)} data-testid="detail-escalate-btn">
                    <ArrowUp size={12} className="mr-1" /> Escalate
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-none text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleDelete(detailTask.id)} data-testid="detail-delete-btn">
                  <Trash size={12} className="mr-1" /> Delete
                </Button>
              </div>

              {/* Comments */}
              <div>
                <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide flex items-center gap-1"><ChatDots size={12} /> Comments ({detailTask.comments?.length || 0})</label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {(detailTask.comments || []).map((c, i) => (
                    <div key={i} className="bg-zinc-50 border border-zinc-200 p-2 text-xs">
                      <div className="flex justify-between">
                        <span className="font-semibold text-zinc-700">{c.user_name}</span>
                        <span className="text-zinc-400">{formatDate(c.timestamp)}</span>
                      </div>
                      <p className="text-zinc-600 mt-0.5">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add a comment..." className="rounded-none border-zinc-300 h-8 text-xs flex-1" data-testid="comment-input" onKeyDown={e => e.key === 'Enter' && handleAddComment()} />
                  <Button size="sm" onClick={handleAddComment} className="bg-zinc-900 text-white rounded-none h-8 text-xs" data-testid="add-comment-btn">Send</Button>
                </div>
              </div>

              {/* Audit Trail */}
              {detailTask.audit_trail && detailTask.audit_trail.length > 0 && (
                <div>
                  <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">Audit Trail</label>
                  <div className="mt-2 space-y-1">
                    {detailTask.audit_trail.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] text-zinc-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 shrink-0"></span>
                        <span className="font-medium text-zinc-700">{a.action}</span>
                        <span>{a.details}</span>
                        <span className="ml-auto text-zinc-400">{a.user_name} &middot; {formatDate(a.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
